import { TraceColorPicker } from "./TraceColorPicker"
import { ModalityConfigurationDiv } from "../../../../../Constants/StyledComponents"
import React, { useContext, useEffect, useRef, useState } from "react"
import { MobergRow, MobergDropdown, MobergButton, MobergButtonShape, MobergIconSize, MobergTheme, MobergButtonVariant, MobergInputLabel, MobergShortTextInput, MobergColumn, MobergCheckbox } from "../../../../../Moberg"
import { equalsAnalyticTrace, LineTraceConfig, LineTraceConfigJSON, RenderStrategy, TraceConfigJSON, TraceOption } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { MdClose, MdSettings } from "react-icons/md"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { EditAnalysisModal } from "../../../../../Pages/Data/Visualize/DataReview/Components/Modals/EditAnalysis/EditAnalysisModal"
import { isQEEGTrendAnalysis, OnDemandAnalysis, QEEGChannelGroupId } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { LineGraphModalitiesContext } from "./LineGraphModalities"
import { QEEGChannelGroupDropdown } from "../QEEGChannelGroupDropdown"
import { withQEEGTrendChannelGroupAndColor } from "../../../../../Constants/LabelSettings"

export const AnalyticLineModalities: React.FC = () => {
	const { traceOptions, currentGraph, reasonAddMeasurementIsDisabled, updateGraphProperty, removeTrace } = useContext(ConfigureWindowModalContext)
	const { activeAnalyticTraces, updateTrace, handleColorChange } = useContext(LineGraphModalitiesContext)
	const [colorPickerTraceId, setColorPickerTraceId] = useState<string>()
	const colorPickerBlockRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
	const colorPickerRef = useRef<HTMLDivElement | null>(null)
	const { createModal } = useModalProvider()
	const analyticTraceOptions = traceOptions.filter((traceOption: TraceOption) =>
		traceOption.value.onDemandAnalysis && traceOption.supportedRenderStrategies.includes(RenderStrategy.LINE)
	)

	function addModality() {
		if (analyticTraceOptions.length === 0) return

		const newTrace: LineTraceConfigJSON = {
			...analyticTraceOptions[0].value,
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.LINE,
			color: "#000",
			isVisible: true
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => ([...previous, newTrace]))
		updateTrace(newTrace.id, newTrace, { useModalitySpecificDefaults: true })
	}

	function toggleColorPicker(event: any, traceId: string) {
		const node = colorPickerRef.current

		if (node?.style.display === "block" && traceId === colorPickerTraceId) {
			node.style.display = "none"
			return
		}

		setColorPickerTraceId(traceId)

		if (node) {
			node.style.display = "block"
			node.style.top = `${event.target.getBoundingClientRect().bottom}px`
			node.style.left = `${event.target.getBoundingClientRect().right}px`
		}
	}

	function addColorPickerBlockRef(node: HTMLDivElement | null, traceId: string) {
		colorPickerBlockRefs.current.set(traceId, node)
	}

	function traceChannelGroupChanged(trace: LineTraceConfig, channelGroupId: QEEGChannelGroupId) {
		updateTrace(trace.id, withQEEGTrendChannelGroupAndColor(trace, channelGroupId))
	}

	function traceAnalysisArgumentsChanged(trace: LineTraceConfig, newArgs: OnDemandAnalysis | undefined) {
		updateTrace(trace.id, {
            ...trace,
			onDemandAnalysis: newArgs,
			dataKey: JSON.stringify(newArgs)
		})
	}

	useEffect(() => {
		const clickHandler = (event: any) => {
			let clickedOutside = true

			colorPickerBlockRefs.current.forEach(node => {
				if (node?.contains(event.target)) {
					clickedOutside = false
				}
			})

			if (colorPickerRef.current?.contains(event.target)) {
				clickedOutside = false
			}

			if (clickedOutside && colorPickerRef.current) {
				colorPickerRef.current.style.display = "none"
			}
		}

		document.addEventListener("click", clickHandler)
		return () => document.removeEventListener("click", clickHandler)
	})

	return (
        <ModalityConfigurationDiv>
			<MobergColumn gap="16px">
				<div style={{ display: "grid", gridTemplateColumns: "auto auto auto auto auto auto 1fr", columnGap: "16px", rowGap: "12px", alignItems: "center" }}>

					<MobergInputLabel text={"Analytic"} />
					<MobergInputLabel text={"Channel group"} />
					<MobergInputLabel text={"Label"} />
					<MobergInputLabel text={"Color"} />
					<MobergInputLabel text={"Calculation"} />
					<MobergInputLabel text={"Visible"} />
					<MobergInputLabel text={""} />

					{activeAnalyticTraces.map(trace => {
						return (
							<>
								<MobergDropdown
									options={analyticTraceOptions}
									onChange={(traceConfig: LineTraceConfigJSON) => updateTrace(trace.id, traceConfig, { useModalitySpecificDefaults: true })}
									selectedValue={trace}
									equals={equalsAnalyticTrace}
									width={250}
								/>

								{isQEEGTrendAnalysis(trace.onDemandAnalysis)
									? <QEEGChannelGroupDropdown
										dataTestId="line-analytic-channel-group-select"
										selectedValue={trace.onDemandAnalysis.channelGroupId}
										onChange={channelGroupId => traceChannelGroupChanged(trace, channelGroupId)}
									/>
									: <div />
								}

								<MobergShortTextInput
									limit={20}
									defaultValue={trace.name}
									value={trace.name}
									onChange={(name: string) => updateTrace(trace.id, { ...trace, name })}
									style={{ width: "250px" }}
								/>

								<MobergRow expand={false}>
									<div
										onClick={event => toggleColorPicker(event, trace.id)}
										style={{
											width: "25px",
											height: "25px",
											backgroundColor: (trace as LineTraceConfigJSON).color ?? "#000",
											border: "1px solid var(--color-border-default)",
											borderRadius: "25px",
											cursor: "pointer",
										}}
										ref={ref => addColorPickerBlockRef(ref, trace.id)}
									/>
								</MobergRow>

								<MobergButton
									tooltip={trace.onDemandAnalysis 
										? "Edit the calculation of " + trace.onDemandAnalysis.analytic 
										: "This modality is a raw signal."}
									shape={MobergButtonShape.SQUARE}
									onClick={() => {
										createModal(<EditAnalysisModal 
											analysis={trace.onDemandAnalysis}
											rawDataKey={trace.rawDataKey}
											onChange={(newArgs: OnDemandAnalysis | undefined) => traceAnalysisArgumentsChanged(trace, newArgs)}
										/>)
									}}
								>
									<MdSettings size={MobergIconSize.REGULAR} />
								</MobergButton>

								<MobergCheckbox
									isChecked={trace.isVisible ?? true}
									onChange={isVisible => updateTrace(trace.id, { ...trace, isVisible })}
								/>

								<MobergButton shape={MobergButtonShape.SQUARE} onClick={() => removeTrace(trace.id)} style={{ padding: "6px" }}>
									<MdClose size={MobergIconSize.REGULAR} />
								</MobergButton>
							</>
						)
					})}

					<MobergButton onClick={addModality} theme={MobergTheme.BLUE} variant={MobergButtonVariant.OUTLINE} style={{ width: "250px" }}
						disabled={!!reasonAddMeasurementIsDisabled || analyticTraceOptions.length === 0}
						tooltip={reasonAddMeasurementIsDisabled ?? (analyticTraceOptions.length === 0 ? "No analytics are available for this workspace." : undefined)}>
						Add analytic
					</MobergButton>
				</div>
			</MobergColumn>


            <div id={`ColorPickerWindow`} ref={colorPickerRef} style={{ position: "fixed", display: "none", zIndex: 999 }}>
                <TraceColorPicker
                    selectedColor={((currentGraph?.traces ?? []).find(trace => trace.id === colorPickerTraceId) as LineTraceConfigJSON)?.color ?? "#000"}
                    onChange={color => {
                        if (colorPickerTraceId !== undefined) {
                            handleColorChange(colorPickerTraceId, color)
                        }
                    }}
                    onClose={() => {
                        if (colorPickerRef.current) colorPickerRef.current.style.display = "none"
                    }}
                />
            </div>
        </ModalityConfigurationDiv>
	)
}
